import {matchesAppleMirrorReceiptOrigin,type AppleMirrorReceiptReference} from '@/lib/appleMirrorReceipt';
import { matchesMirrorConnectedMessageReceiptOrigin, type MirrorConnectedMessageReceiptReference } from "@/lib/mirrorConnectedMessageReceipt";
import { matchesMirrorConnectedTaskReceiptOrigin, type MirrorConnectedTaskReceiptReference } from "@/lib/mirrorConnectedTaskReceipt";
import { matchesMirrorFileReceiptOrigin, type MirrorFileReceiptReference } from "@/lib/mirrorFileReceipt";
import { matchesMirrorFinanceReceiptOrigin, type FinanceReceipt } from '@/lib/financeExecutor';
import type { MirrorFitnessReceiptReference } from "@/lib/fitnessExecutor";
import { matchesMirrorBodyHealthReceiptOrigin, type MirrorBodyHealthReceiptReference } from "@/lib/bodyHealthExecutor";
import { matchesMirrorSleepReceiptOrigin, type MirrorSleepReceiptReference } from "@/lib/sleepExecutor";
import { matchesMirrorCheckinReceiptOrigin, type MirrorCheckinReceiptReference } from "@/lib/checkinExecutor";
import { matchesMirrorFocusReceiptOrigin, type MirrorFocusReceiptReference } from "@/lib/focusExecutor";
import { matchesMirrorReminderReceiptOrigin, type MirrorReminderReceiptReference } from "@/lib/reminderExecutor";
import { matchesMirrorAgendaReceiptOrigin, type MirrorAgendaReceiptReference } from "@/lib/agendaExecutor";
import { matchesMirrorHabitReceiptOrigin, type MirrorHabitReceiptReference } from "@/lib/habitExecutor";
import { matchesMirrorNoteReceiptOrigin, type MirrorNoteReceiptReference } from "@/lib/noteExecutor";
import { matchesMirrorGoalReceiptOrigin, type MirrorGoalReceiptReference } from "@/lib/goalExecutor";
// useModuleCapture — the reusable spine of universal capture.
//
// Lives OUTSIDE the mirror-chat edge function by design (firm architecture
// decision): mirror-chat only EMITS structured proposals; this hook CONSUMES
// one and routes it through a dispatcher (registry) -> module extractor ->
// confirm modal -> the modal writes the module's table.
//
// Mount it ONCE at the seam where a mirror-chat response brings proposals
// (MirrorChat). `routeProposals` dispatches the proposals a handler claims
// (e.g. agenda) and returns the unhandled remainder for the legacy action-chip
// pipeline. `modal` renders the active module's confirm modal.
//
// Generic plumbing ported verbatim from MirrorChat's previous inline agenda
// path so behavior is identical:
//   - resolvingRef        : action_id Set — catches same-turn SSE replays.
//   - resolvedRawTextsRef : normalized raw_text -> ts (90s TTL) — catches
//                           Sonnet re-emitting the same intent on a follow-up
//                           turn with a fresh action_id (Vector 3 defense).

import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useQuotaErrorSurface } from "@/hooks/useQuotaErrorSurface";
import type { ActionProposal, ActionTool } from "@/types/mirror";
import type { CaptureContext, ModuleCaptureHandler } from "@/lib/capture/types";
import { MODULE_CAPTURE_HANDLERS } from "@/lib/capture/registry";
import { matchesMirrorEmailReceiptOrigin, type MirrorEmailReceiptReference } from "@/lib/mirrorEmailReceipt";
import { matchesMirrorCalendarReceiptOrigin, type MirrorCalendarReceiptReference } from "@/lib/calendarAction";
import { matchesMirrorContactReceiptOrigin, type MirrorContactReceiptReference } from "@/lib/contactExecutor";
import { matchesMirrorExternalTaskReceiptOrigin, type MirrorExternalTaskReceiptReference } from "@/lib/externalTaskExecutor";
import { matchesMirrorTaskReceiptOrigin, type MirrorTaskReceiptReference } from "@/lib/taskExecutor";

// Cross-turn re-emit guard window. Matches MirrorChat's prior 90s value.
const RESOLVED_RAW_TEXT_TTL_MS = 90 * 1000;

interface ActiveCapture {
  // Per-capture id (the proposal's action_id) — keys the modal so a NEW capture
  // for the SAME tool remounts with fresh prefill instead of reusing old state.
  id: string;
  tool: ActionTool;
  prefill: unknown;
  // The normalized-source raw_text, kept so close can release the re-emit guard
  // (lets an intentional retry of the same phrase re-open the modal).
  rawText: string;
  ownerId: string | null;
  sessionId: string | null;
  generation: number;
  requestId?: number;
}

export interface UseModuleCaptureOptions {
  ownerId?: string | null;
  sessionId?: string | null;
  getRequestId?: () => number;
  // Fired after a handler's confirm modal reports a successful write. Optional
  // per mount (MirrorChat passes nothing — its prior onCreated was a no-op).
  onCaptured?: (tool: ActionTool, result?: MirrorConnectedMessageReceiptReference | MirrorConnectedTaskReceiptReference | MirrorFileReceiptReference | FinanceReceipt | MirrorFitnessReceiptReference | MirrorEmailReceiptReference | AppleMirrorReceiptReference | MirrorCalendarReceiptReference | MirrorContactReceiptReference | MirrorExternalTaskReceiptReference | MirrorGoalReceiptReference | MirrorTaskReceiptReference | MirrorNoteReceiptReference | MirrorHabitReceiptReference | MirrorAgendaReceiptReference | MirrorReminderReceiptReference | MirrorFocusReceiptReference | MirrorCheckinReceiptReference | MirrorSleepReceiptReference | MirrorBodyHealthReceiptReference) => void | Promise<boolean>;
}

export interface UseModuleCaptureReturn {
  // Dispatch the proposals a handler claims (-> extractor -> confirm modal) and
  // return the UNHANDLED remainder for the caller's chip pipeline.
  routeProposals: (proposals: ActionProposal[], latestUserText?: string) => ActionProposal[];
  // The active module's confirm modal(s). Render once in the tree.
  modal: ReactNode;
}

export function useModuleCapture(
  options?: UseModuleCaptureOptions,
): UseModuleCaptureReturn {
  const { lang, t } = useLanguage();
  const surfaceQuota = useQuotaErrorSurface();

  const resolvingRef = useRef<Set<string>>(new Set());
  const resolvedRawTextsRef = useRef<Map<string, number>>(new Map());
  // A QUEUE, not a single slot: rapid sequential captures must NEVER overwrite
  // each other. The old single `active` dropped every capture but the last one
  // the user managed to confirm (the 2/13 write-loss). The front of the queue is
  // the open modal; confirming/dismissing it pops to the next.
  const [queue, setQueue] = useState<ActiveCapture[]>([]);
  const ownerId = options?.ownerId ?? null;
  const sessionId = options?.sessionId ?? null;
  const lifecycle = useRef({ ownerId, sessionId, generation: 0, mounted: true });
  // Invalidate synchronously so a render for another identity cannot display
  // the previous queue while effect cleanup is still waiting.
  if (lifecycle.current.ownerId !== ownerId || lifecycle.current.sessionId !== sessionId) {
    lifecycle.current = { ownerId, sessionId, generation: lifecycle.current.generation + 1, mounted: true };
    resolvingRef.current.clear();
    resolvedRawTextsRef.current.clear();
  }
  const requestGetter = useRef(options?.getRequestId);
  requestGetter.current = options?.getRequestId;
  const generation = lifecycle.current.generation;
  const active = queue.find(capture => capture.generation === generation &&
    capture.ownerId === ownerId && capture.sessionId === sessionId && (capture.requestId === undefined || capture.requestId === requestGetter.current?.())) ?? null;
  const activeRef = useRef<ActiveCapture | null>(active);
  activeRef.current = active;
  const isCurrentCapture = useCallback((capture: Pick<ActiveCapture, "ownerId" | "sessionId" | "generation"> & {requestId?: number}) =>
    lifecycle.current.mounted && capture.generation === lifecycle.current.generation &&
    capture.ownerId === lifecycle.current.ownerId && capture.sessionId === lifecycle.current.sessionId && (capture.requestId === undefined || capture.requestId === requestGetter.current?.()), []);
  useEffect(() => {
    lifecycle.current.mounted = true;
    return () => { lifecycle.current.mounted = false; };
  }, []);

  // Keep `t` and `onCaptured` in refs so the dispatch closure doesn't depend on
  // them (avoids re-creating it on every i18n / parent render).
  const tRef = useRef(t);
  tRef.current = t;
  const onCapturedRef = useRef(options?.onCaptured);
  onCapturedRef.current = options?.onCaptured;

  const handlersByTool = useMemo(() => {
    const map = new Map<ActionTool, ModuleCaptureHandler>();
    for (const h of MODULE_CAPTURE_HANDLERS) map.set(h.tool, h);
    return map;
  }, []);

  const dispatch = useCallback(
    async (handler: ModuleCaptureHandler, proposal: ActionProposal, latestUserText?: string) => {
      const financial = proposal.tool === "propose_finance_action" || proposal.tool === "propose_finance_log" || proposal.tool === "propose_file_action" || proposal.tool === "propose_connected_task_action" || proposal.tool === "propose_connected_message_action";
      const origin = { ownerId, sessionId, generation, ...(financial ? {requestId:requestGetter.current?.()} : {}) };
      if (!isCurrentCapture(origin)) return;
      if (handler.isEnabled && !handler.isEnabled()) return;

      const actionId = proposal.action_id;
      // Same-turn replay guard (SSE replays / strict-mode double-renders).
      if (resolvingRef.current.has(actionId)) return;
      resolvingRef.current.add(actionId);

      const rawText = handler.getRawText(proposal);
      if (!rawText) {
        resolvingRef.current.delete(actionId);
        return;
      }

      const isCalendarCreation = proposal.tool === "propose_agenda_event" || proposal.tool === "create_agenda_event" ||
        (proposal.tool === "propose_local_agenda_action" || proposal.tool === "propose_calendar_action") && proposal.args.action === "create";
      let quotaSurfaced = false;
      const failedExtraction = () => {
        if (!isCurrentCapture(origin) || !isCalendarCreation) return;
        // No review was opened and no writer ran. Permit an intentional retry
        // while retaining the guards for in-flight and successful captures.
        resolvingRef.current.delete(actionId);
        if (!quotaSurfaced) toast.error(tRef.current("agenda.failedToCreate"));
      };
      const ctx: CaptureContext = { lang, t: tRef.current, surfaceQuota: async error => {
        const surfaced = await surfaceQuota(error);
        quotaSurfaced ||= surfaced;
        return surfaced;
      },
        ownerId: origin.ownerId, sessionId: origin.sessionId, latestUserText, assertCurrent: () => { if (!isCurrentCapture(origin)) throw new Error("invalid_session"); } };
      try {
        const prefill = await handler.extract(rawText, proposal, ctx);
        if (prefill == null) { failedExtraction(); return; }
        if (!isCurrentCapture(origin)) return;

        // Vector 3 defense: skip if this exact (normalized) raw_text already
        // opened a modal within the TTL window.
        const normalized = rawText.trim().toLowerCase();
        const now = Date.now();
        const existing = resolvedRawTextsRef.current.get(normalized);
        if (existing !== undefined && now - existing < RESOLVED_RAW_TEXT_TTL_MS) {
          console.log(
            "[useModuleCapture] re-emit detected for already-processed raw_text, skipping modal",
          );
          return;
        }
        resolvedRawTextsRef.current.set(normalized, now);

        // Enqueue (do NOT overwrite a modal the user hasn't confirmed yet).
        setQueue((q) => [...q.filter(capture => capture.generation === origin.generation),
          { id: actionId, tool: handler.tool, prefill, rawText, ...origin }]);
      } catch (err) {
        console.warn("[useModuleCapture] extract threw for tool=" + handler.tool, err);
        failedExtraction();
      }
    },
    [lang, surfaceQuota, isCurrentCapture, ownerId, sessionId, generation],
  );

  const routeProposals = useCallback(
    (proposals: ActionProposal[], latestUserText?: string): ActionProposal[] => {
      const unhandled: ActionProposal[] = [];
      for (const original of proposals) {
        // Persisted cloud-contact proposals enter the same reviewed executor.
        // Apple contacts keep their device bridge. Legacy full names never
        // become an inferred given/family-name edit.
        const proposal: ActionProposal = original.tool === "propose_people_task_action" && original.args.kind === "contact" && original.args.provider !== "apple_contacts"
          ? {tool:"propose_contact_action",action_id:original.action_id,preview:original.preview,args:{
            action:["search","create","update","delete"].includes(original.args.action) ? original.args.action as "search"|"create"|"update"|"delete" : "search",
            provider:original.args.provider === "google_contacts" || original.args.provider === "microsoft_contacts" ? original.args.provider : "auto",
            query:original.args.query,item_id:original.args.item_id,given_name:original.args.action === "create" ? original.args.title : undefined,
            email:original.args.email,phone:original.args.phone}}
          : original;
        const handler = handlersByTool.get(proposal.tool);
        if (handler) {
          // Fire-and-forget: extract latency must not block the chat/TTS path
          // (matches the prior `void resolveAgendaIntent(intent)`).
          void dispatch(handler, proposal, latestUserText);
        } else {
          unhandled.push(proposal);
        }
      }
      return unhandled;
    },
    [handlersByTool, dispatch],
  );

  const closeCapture = (capture: ActiveCapture) => {
    if (!isCurrentCapture(capture) || activeRef.current?.id !== capture.id) return;
    activeRef.current = null;
    resolvedRawTextsRef.current.delete(capture.rawText.trim().toLowerCase());
    // Pop the FRONT (the modal just confirmed/dismissed); the next queued
    // capture becomes active and its modal opens. Never drops the rest.
    setQueue((q) => {
      return q.filter(entry => entry.generation === capture.generation && entry.id !== capture.id);
    });
  };

  // Render every handler's modal, opened only for the active one. Keeping them
  // all mounted (open=false when inactive) preserves exit animations and
  // matches the prior always-rendered <CreateEventModal open={...}/>.
  const modal: ReactNode = (
    <>
      {MODULE_CAPTURE_HANDLERS.map((handler) => {
        const isActive = active != null && active.tool === handler.tool;
        return (
          <Fragment key={isActive ? `${handler.tool}-${active!.id}` : handler.tool}>
            {handler.renderModal({
              open: isActive,
              prefill: isActive ? active!.prefill : null,
              isCurrent: () => !!active && isCurrentCapture(active) && activeRef.current?.id === active.id,
              onClose: () => { if (isActive && active) closeCapture(active); },
              onCreated: (result) => {
                if (!isActive || !active || !isCurrentCapture(active) || activeRef.current?.id !== active.id) return;
                if (handler.tool === "propose_finance_action" || handler.tool === "propose_finance_log") {
                  if (!active.ownerId || !active.sessionId || !matchesMirrorFinanceReceiptOrigin(result,
                    {userId:active.ownerId,sessionId:active.sessionId,actionId:active.id}) || result.operation_id !== (active.prefill as {operation_id?:unknown})?.operation_id) return Promise.resolve(false);
                  return Promise.resolve(onCapturedRef.current?.("propose_finance_action",result)).then(ack =>
                    isCurrentCapture(active) && activeRef.current?.id === active.id && ack === true);
                } else if (handler.tool === "propose_connected_message_action") {
                  if (!active.ownerId || !active.sessionId || !matchesMirrorConnectedMessageReceiptOrigin(result, {userId:active.ownerId,sessionId:active.sessionId,actionId:active.id})) return Promise.resolve(false);
                  return Promise.resolve(onCapturedRef.current?.(handler.tool,result)).then(ack => isCurrentCapture(active) && activeRef.current?.id === active.id && ack === true);
                } else if (handler.tool === "propose_connected_task_action") {
                  if (!active.ownerId || !active.sessionId || !matchesMirrorConnectedTaskReceiptOrigin(result, {userId:active.ownerId,sessionId:active.sessionId,actionId:active.id})) return Promise.resolve(false);
                  return Promise.resolve(onCapturedRef.current?.(handler.tool,result)).then(ack => isCurrentCapture(active) && activeRef.current?.id === active.id && ack === true);
                } else if (handler.tool === "propose_file_action") {
                  if (!active.ownerId || !active.sessionId || !matchesMirrorFileReceiptOrigin(result,
                    { userId: active.ownerId, sessionId: active.sessionId, actionId: active.id })) return Promise.resolve(false);
                  return Promise.resolve(onCapturedRef.current?.(handler.tool, result)).then(ack =>
                    isCurrentCapture(active) && activeRef.current?.id === active.id && ack === true);
                } else if (handler.tool === "propose_email_action") {
                  if (!active.ownerId || !active.sessionId || !matchesMirrorEmailReceiptOrigin(result,
                    { userId: active.ownerId, sessionId: active.sessionId, actionId: active.id })) return;
                  onCapturedRef.current?.(handler.tool, result);
                } else if (handler.tool === "propose_calendar_action") {
                  if (!active.ownerId || !active.sessionId) return Promise.resolve(false);
                  const origin = { userId:active.ownerId, sessionId:active.sessionId, actionId:active.id };
                  const localDraft = (active.prefill as { vyv_draft?: { operation_id?: string; event_id?: string } }).vyv_draft;
                  if (matchesMirrorAgendaReceiptOrigin(result, origin) && result.action === "create" &&
                    result.operation_id === localDraft?.operation_id && result.event_id === localDraft.event_id) {
                    return onCapturedRef.current?.("propose_local_agenda_action", result) ?? Promise.resolve(false);
                  }
                  if(matchesAppleMirrorReceiptOrigin(result,origin))return onCapturedRef.current?.(handler.tool,result)??Promise.resolve(false);
                  if (!matchesMirrorCalendarReceiptOrigin(result, origin)) return Promise.resolve(false);
                  return onCapturedRef.current?.(handler.tool,result) ?? Promise.resolve(false);
                } else if (handler.tool === "propose_contact_action" || handler.tool === "propose_people_task_action" && matchesMirrorContactReceiptOrigin(result,
                  {userId:active.ownerId ?? "",sessionId:active.sessionId ?? "",actionId:active.id})) {
                  if (!active.ownerId || !active.sessionId || !matchesMirrorContactReceiptOrigin(result,
                    {userId:active.ownerId,sessionId:active.sessionId,actionId:active.id})) return;
                  return onCapturedRef.current?.("propose_contact_action",result) ?? Promise.resolve(false);
                } else if (handler.tool === "propose_people_task_action" && (active.prefill as {provider?: string})?.provider !== "apple_reminders" && (active.prefill as {kind?: string})?.kind !== "contact") {
                  if (!active.ownerId || !active.sessionId || !matchesMirrorExternalTaskReceiptOrigin(result,
                    {userId:active.ownerId,sessionId:active.sessionId,actionId:active.id})) return Promise.resolve(false);
                  return onCapturedRef.current?.(handler.tool,result) ?? Promise.resolve(false);
                } else if (handler.tool === "propose_body_log" || handler.tool === "propose_body_health_action") {
                  if (!active.ownerId || !active.sessionId || !matchesMirrorBodyHealthReceiptOrigin(result,
                    { userId: active.ownerId, sessionId: active.sessionId, actionId: active.id }) || result.operation_id !== (active.prefill as { operation_id?: unknown })?.operation_id) return Promise.resolve(false);
                  return Promise.resolve(onCapturedRef.current?.("propose_body_health_action",result)).then(ack =>
                    isCurrentCapture(active) && activeRef.current?.id === active.id && ack === true);
                } else if (handler.tool === "propose_sleep_log" || handler.tool === "propose_sleep_action") {
                  if (!active.ownerId || !active.sessionId || !matchesMirrorSleepReceiptOrigin(result,
                    { userId: active.ownerId, sessionId: active.sessionId, actionId: active.id }) || result.operation_id !== (active.prefill as { operation_id?: unknown })?.operation_id) return Promise.resolve(false);
                  return Promise.resolve(onCapturedRef.current?.("propose_sleep_action",result)).then(ack =>
                    isCurrentCapture(active) && activeRef.current?.id === active.id && ack === true);
                } else if (handler.tool === "propose_checkin_action") {
                  if (!active.ownerId || !active.sessionId || !matchesMirrorCheckinReceiptOrigin(result,
                    { userId: active.ownerId, sessionId: active.sessionId, actionId: active.id })) return Promise.resolve(false);
                  return Promise.resolve(onCapturedRef.current?.("propose_checkin_action",result)).then(ack =>
                    isCurrentCapture(active) && activeRef.current?.id === active.id && ack === true);
                } else if (["propose_focus_log", "propose_focus_action", "propose_focus_timer_action"].includes(handler.tool)) {
                  if (!active.ownerId || !active.sessionId || !matchesMirrorFocusReceiptOrigin(result,
                    {userId:active.ownerId,sessionId:active.sessionId,actionId:active.id})) return Promise.resolve(false);
                  return onCapturedRef.current?.("propose_focus_action",result) ?? Promise.resolve(false);
                } else if (["propose_reminder_action", "propose_reminder_preferences_action"].includes(handler.tool)) {
                  if (!active.ownerId || !active.sessionId || !matchesMirrorReminderReceiptOrigin(result,
                    {userId:active.ownerId,sessionId:active.sessionId,actionId:active.id})) return Promise.resolve(false);
                  return onCapturedRef.current?.("propose_reminder_action",result) ?? Promise.resolve(false);
                } else if (["propose_agenda_event", "propose_local_agenda_action", "create_agenda_event"].includes(handler.tool)) {
                  if (!active.ownerId || !active.sessionId || !matchesMirrorAgendaReceiptOrigin(result,
                    {userId:active.ownerId,sessionId:active.sessionId,actionId:active.id})) return Promise.resolve(false);
                  return onCapturedRef.current?.("propose_local_agenda_action",result) ?? Promise.resolve(false);
                } else if (["propose_habit_log", "propose_habit_action", "create_habit"].includes(handler.tool)) {
                  if (!active.ownerId || !active.sessionId || !matchesMirrorHabitReceiptOrigin(result,
                    {userId:active.ownerId,sessionId:active.sessionId,actionId:active.id})) return Promise.resolve(false);
                  return onCapturedRef.current?.("propose_habit_action",result) ?? Promise.resolve(false);
                } else if (handler.tool === "create_note" || handler.tool === "propose_note_action") {
                  if (!active.ownerId || !active.sessionId || !matchesMirrorNoteReceiptOrigin(result,
                    { userId: active.ownerId, sessionId: active.sessionId, actionId: active.id })) return Promise.resolve(false);
                  return onCapturedRef.current?.("propose_note_action",result) ?? Promise.resolve(false);
                } else if (handler.tool === "create_task" || handler.tool === "propose_local_task_action") {
                  if (!active.ownerId || !active.sessionId || !matchesMirrorTaskReceiptOrigin(result,
                    {userId:active.ownerId,sessionId:active.sessionId,actionId:active.id})) return Promise.resolve(false);
                  return onCapturedRef.current?.("propose_local_task_action",result) ?? Promise.resolve(false);
                } else if (handler.tool === "propose_goal" || handler.tool === "propose_goal_action") {
                  if (!active.ownerId || !active.sessionId || !matchesMirrorGoalReceiptOrigin(result,
                    {userId:active.ownerId,sessionId:active.sessionId,actionId:active.id})) return Promise.resolve(false);
                  return onCapturedRef.current?.(handler.tool,result) ?? Promise.resolve(false);
                } else onCapturedRef.current?.(handler.tool);
              },
            })}
          </Fragment>
        );
      })}
    </>
  );

  return { routeProposals, modal };
}
